import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowRight, RefreshCw } from "lucide-react";

export default function VerifyEmailPage() {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError("");

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (newCode.every((d) => d) && newCode.join("").length === 6) {
      handleVerify(newCode.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split("");
      setCode(newCode);
      handleVerify(pasted);
    }
  };

  const handleVerify = async (codeStr) => {
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/verify-email", { code: codeStr });
      await refreshUser();
      const dest = user?.role === "artist" ? "/dashboard" : "/discover";
      navigate(dest);
    } catch (err) {
      setError(err.response?.data?.detail || "Code incorrect");
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await api.post("/auth/resend-verification");
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSkip = () => {
    const dest = user?.role === "artist" ? "/dashboard" : "/discover";
    navigate(dest);
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center px-4" data-testid="verify-email-page">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#FF6B35] opacity-[0.06] blur-[120px]" />

      <div className="w-full max-w-sm relative z-10 text-center">
        <div className="w-16 h-16 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/20 flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-[#FF6B35]" />
        </div>

        <h1 className="font-heading font-bold text-2xl text-foreground mb-2">Verifie ton email</h1>
        <p className="text-sm text-muted-foreground mb-8">
          Un code a 6 chiffres a ete envoye a<br />
          <span className="text-[#AFA8BA] font-medium">{user?.email}</span>
        </p>

        {/* Code inputs */}
        <div className="flex gap-2.5 justify-center mb-6" onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-12 h-14 text-center text-xl font-mono-credits font-bold bg-foreground/5 border border-foreground/10 rounded-xl text-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none transition-colors"
              data-testid={`verify-code-${i}`}
            />
          ))}
        </div>

        {error && (
          <p className="text-sm text-red-400 mb-4" data-testid="verify-error">{error}</p>
        )}

        {loading && (
          <p className="text-sm text-[#FF6B35] mb-4">Verification...</p>
        )}

        {/* Resend */}
        <div className="mb-6">
          {resendCooldown > 0 ? (
            <p className="text-xs text-muted-foreground">
              Renvoyer dans {resendCooldown}s
            </p>
          ) : (
            <button
              onClick={handleResend}
              disabled={resendLoading}
              className="flex items-center gap-1.5 text-sm text-[#FF6B35] hover:text-[#FF8FA3] transition-colors mx-auto disabled:opacity-50"
              data-testid="resend-code-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${resendLoading ? "animate-spin" : ""}`} />
              Renvoyer le code
            </button>
          )}
        </div>

        {/* Skip */}
        <button
          onClick={handleSkip}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[#AFA8BA] transition-colors mx-auto"
          data-testid="skip-verify-btn"
        >
          Passer pour l'instant
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="noise-overlay" />
    </div>
  );
}
